import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Verify vs enrich. Fixture `verifyVsEnrich`: the
// officer has a footage seat. The scene opens on a Valley Mart report written
// in Verify mode (two "[Footage check" suggestions), shows the two modes in the
// upload modal (the interview sample audio stands in for the mp4, as in
// body-cam-footage; nothing is submitted), then visits a report written in
// Enrich mode, whose narrative already holds the camera's observations.
const VERIFY_CASE = "c0000000-0000-4000-8000-000000000221";
const TOGGLE = `${sel("uploadVideoAnalysis")} input[type="checkbox"]`;
const CLOSE_MODAL = `${sel("uploadModal")} > div:first-child > button`;
const ENRICH_CARD = `${sel("caseListItem")}:nth-child(2)`;
const SECOND_SUGGESTION = `${sel("suggestionItem")} + ${sel("suggestionItem")}`;

const scene: Scene = {
  id: "verify-vs-enrich",
  title: "Verify vs enrich",
  startRoute: `/cases/${VERIFY_CASE}`,
  fixture: "verifyVsEnrich",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "With the footage add-on, the AI doesn't only hear your body-cam video, it watches it. What happens with what it saw comes down to one choice you make at upload: Verify or Enrich. Let me show you both.",
      actions: [
        { type: "waitFor", target: sel("reportScore") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "upload",
      narration: "I'll click New Case, choose Upload Files, and add the video.",
      actions: [
        // "New Case" at 0.5 s, "Upload Files" at 1.6 s, "add the video" at 3.0 s.
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 200 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 300 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/interaction-witness-interview.mp3`,
          name: "bodycam-valley-mart.mp4",
          mime: "video/mp4",
        },
        { type: "waitFor", target: sel("uploadVideoAnalysis") },
        { type: "wait", ms: 600 },
      ],
    },
    {
      id: "toggle",
      narration: "On the video's row, I turn on Analyze the footage. Two choices appear underneath.",
      actions: [
        // "I turn on Analyze the footage" at 1.3 s, "Two choices" at 3.7 s.
        { type: "scrollIntoView", target: sel("uploadVideoAnalysis") },
        { type: "moveTo", target: TOGGLE },
        { type: "spotlight", target: sel("uploadVideoAnalysis") },
        { type: "wait", ms: 700 },
        { type: "click", target: TOGGLE },
        { type: "waitFor", target: sel("uploadVideoVerify") },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "verify",
      narration:
        "The first is Check the draft against the footage. That's Verify. The narrative is written from your audio exactly as usual, and the footage is used afterward as a second reviewer.",
      actions: [
        { type: "moveTo", target: sel("uploadVideoVerify") },
        { type: "spotlight", target: sel("uploadVideoVerify") },
        { type: "wait", ms: 10000 },
      ],
    },
    {
      id: "enrich",
      narration:
        "The second is Write footage observations into the draft. That's Enrich. The narrative waits for the footage to be read first, so it can include what the camera shows. It's the one to pick for silent footage.",
      actions: [
        { type: "moveTo", target: sel("uploadVideoEnrich") },
        { type: "spotlight", target: sel("uploadVideoEnrich") },
        { type: "wait", ms: 11500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "verify-result",
      narration:
        "Here's what Verify gives you. The draft is still mine, and any differences from the video arrive as suggestions that start with Footage check. This one says the camera shows a second person at the east entrance that my report never mentions. Another says the camera's timestamp doesn't match the time I wrote. I decide what to change.",
      actions: [
        // Close the modal first, then: first suggestion at 7.1 s, second at 13.7 s.
        { type: "click", target: CLOSE_MODAL },
        { type: "waitFor", target: sel("suggestionItem") },
        { type: "wait", ms: 5200 },
        { type: "moveTo", target: sel("suggestionItem") },
        { type: "spotlight", target: sel("suggestionItem") },
        { type: "wait", ms: 6200 },
        { type: "moveTo", target: SECOND_SUGGESTION },
        { type: "spotlight", target: SECOND_SUGGESTION },
        { type: "wait", ms: 4200 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "enrich-result",
      narration:
        "And here's an Enrich report. There are no footage checks, because what the camera saw is already in the narrative: the backpack in his hands, and a second person near the east entrance. The trade-off is time, since the draft can't start until the footage has been read.",
      actions: [
        // The observation paragraph is spotlit at 6.4 s ("what the camera saw").
        // The editor only loads a case when its card is clicked (a route change alone keeps the old one).
        { type: "moveTo", target: ENRICH_CARD },
        { type: "wait", ms: 200 },
        { type: "click", target: ENRICH_CARD },
        { type: "waitFor", target: sel("reportScore") },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: `${sel("editorNarrative")} p:nth-of-type(3)` },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: null },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "outro",
      narration:
        "So Verify is the safe default: your words stay yours, and the camera checks them. Choose Enrich when there's little or no narration and the footage has to carry the report.",
      actions: [{ type: "wait", ms: 2500 }],
    },
  ],
};

export default scene;
